import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  makeStyles,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@material-ui/core";
import moment from "moment";
import Pagination from "@material-ui/lab/Pagination";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useQuery, useMutation, useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";

import { Card, CardContent, CardActions } from "../../components/Card";
import Table from "../../components/Table";
import Button from "../../components/Button";
import { useAlert } from "../../components/Alert";
import {
  DateInput,
  NumberInput,
  Select,
  TextInput,
} from "../../components/Form";
import LoadingModal from "../../components/LoadingModal";

const GET_TRACKRECORDS = gql`
  query trackRecords($id: Int!, $latest: Boolean, $pageSize: Int, $page: Int) {
    trackRecords(id: $id, latest: $latest, pageSize: $pageSize, page: $page) {
      pageCount
      contents {
        id
        date
        reservation
        customerName
        status
        content
        staffId {
          id
          fullName
        }
        createdAt
      }
    }
  }
`;

const GET_COURSES = gql`
  query courses($latest: Boolean, $pageSize: Int, $page: Int) {
    courses(latest: $latest, pageSize: $pageSize, page: $page) {
      futureContents {
        id
        name
        dateStart
      }
    }
  }
`;

const SAVE_TRACKRECORD = gql`
  mutation saveTrackRecord($trackRecordInput: TrackRecordInput!) {
    saveTrackRecord(trackRecordInput: $trackRecordInput)
  }
`;
const EDIT_TRACKRECORDSTATUS = gql`
  mutation editTrackRecordStatus($id: Int!) {
    editTrackRecordStatus(id: $id)
  }
`;
const SIGNUPCOURSE = gql`
  mutation signUpCourse($signUpCourseInputs: [SignUpCourseInput]!) {
    signUpCourse(signUpCourseInputs: $signUpCourseInputs)
  }
`;

export default function TrackRecordTable({ id, name }) {
  const [open, setOpen] = useState(false);
  const [openSignUp, setOpenSignUp] = useState(false);
  const [page, setPage] = useState(1);
  const statusArray = [
    { label: "待追蹤", value: "TO_BE_TRACKED" },
    { label: "電敗", value: "MISSED_CALL" },
    { label: "拒絕", value: "REJECTED" },
    { label: "來電", value: "INCOMING_CALL" },
    { label: "面談", value: "INTERVIEW" },
    { label: "預約", value: "RESERVED" },
  ];
  const useStyles = makeStyles({
    DocumentContainerButtonEnd: {
      display: "flex",
      justifyContent: "flex-end",
    },
    DocumentContainerBetween: {
      alignItems: "center",
      //flexDirection: "row",
      justifyContent: "space-between",
    },
  });
  const classes = useStyles();
  const Alert = useAlert();
  const [getTrackRecords, { data, loading }] = useLazyQuery(GET_TRACKRECORDS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError(error) {
      Alert.alert("通知", `${error}`, [
        {
          text: "確定",
          type: "ok",
        },
      ]);
    },
  });

  const [editTrackRecordStatus, { loading: deleteTrackRecordLoading }] =
    useMutation(EDIT_TRACKRECORDSTATUS, {
      onCompleted({ editTrackRecordStatus }) {
        if (editTrackRecordStatus) {
          Alert.alert("", "取消成功", [
            {
              text: "確定",
              onPress: () =>
                getTrackRecords({
                  variables: { id, latest: true, pageSize: 5, page },
                }),
              type: "ok",
            },
          ]);
        } else {
          Alert.alert("", "取消失敗，請再次嘗試。", [
            { text: "確定", type: "ok" },
          ]);
        }
      },
      onError(error) {
        Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
      },
    });
  useEffect(() => {
    if (id) {
      getTrackRecords({ variables: { id, latest: true, pageSize: 5, page } });
    }
  }, [id, page]);
  if (loading) {
    return (
      <Grid container justify="center">
        <CircularProgress color="secondary" />
      </Grid>
    );
  } else {
    return (
      <>
        <LoadingModal loading={deleteTrackRecordLoading} />
        <CardContent>
          <Grid
            container
            spacing={1}
            classes={{ root: classes.DocumentContainerBetween }}
          >
            <Typography style={{ fontSize: 25 }}>追蹤紀錄</Typography>
            <Grid
              xs={12}
              sm={4}
              item
              classes={{ root: classes.DocumentContainerButtonEnd }}
            >
              <Button
                size="small"
                color="primary"
                variant="contained"
                fullWidth
                onPress={() => {
                  setOpenSignUp(true);
                }}
              >
                報名上課
              </Button>
              <SignUpDialog
                open={openSignUp}
                name={name}
                id={id}
                onClose={() => {
                  setOpenSignUp(false);
                }}
              />
              {"\u2002"}
              <Button
                size="small"
                color="primary"
                variant="contained"
                fullWidth
                onPress={() => {
                  setOpen(true);
                }}
              >
                新增追蹤
              </Button>
              <TrackRecordDialog
                id={id}
                name={name}
                open={open}
                onClose={(v) => {
                  if (v === "refetch")
                    getTrackRecords({
                      variables: { id, latest: true, pageSize: 5, page },
                    });
                  setOpen(false);
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Table
            data={data?.trackRecords.contents}
            tableWidth={2}
            header={[
              "追蹤日期",
              "預約日期",
              "客戶姓名",
              "追蹤狀態",
              "追蹤內容",
              "追蹤業務",
              "操作",
            ]}
            columns={[
              (item) => moment(item.date).format("yyyy-MM-DD"),
              (item) =>
                !!item.reservation
                  ? moment(item.reservation).format("yyyy-MM-DD")
                  : null,
              (item) => item.customerName,
              (item) =>
                statusArray.find((item2) => item2.value === item.status)
                  ?.label ?? "取消預約",
              (item) => item.content,
              (item) => item.staffId.fullName,
              (item) => {
                {
                  return (
                    item.status === "RESERVED" && (
                      <Button
                        variant="contained"
                        color="primary"
                        onPress={() => {
                          Alert.alert("通知", "確定要取消？", [
                            {
                              text: "確定",
                              onPress: () => {
                                editTrackRecordStatus({
                                  variables: { id: item.id },
                                });
                              },
                              type: "ok",
                            },
                            {
                              text: "取消",
                              type: "cancel",
                            },
                          ]);
                        }}
                      >
                        取消預約
                      </Button>
                    )
                  );
                }
              },
            ]}
          />
        </CardContent>
        <CardActions>
          <Grid container justifyContent="center">
            <Pagination
              count={
                data?.trackRecords.pageCount ? data.trackRecords.pageCount : 1
              }
              color="primary"
              size="large"
              page={page}
              onChange={(e, n) => setPage(n)}
            />
          </Grid>
        </CardActions>
      </>
    );
  }
}

function TrackRecordDialog({ id, name, open, onClose = () => {} }) {
  const Alert = useAlert();
  const statusArray = [
    { label: "待追蹤", value: "TO_BE_TRACKED" },
    { label: "電敗", value: "MISSED_CALL" },
    { label: "拒絕", value: "REJECTED" },
    { label: "來電", value: "INCOMING_CALL" },
    { label: "面談", value: "INTERVIEW" },
    { label: "預約", value: "RESERVED" },
  ];
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      date: new Date(),
      reservation: null,
      customerName: "",
      customerId: null,
      userId: null,
      statusObject: {},
      status: "",
      content: "",
      staffId: null,
      staff: "",
      cycle: "",
    },
  });

  const status = useWatch({ control, name: "status" });

  const [saveTrackRecord, { loading }] = useMutation(SAVE_TRACKRECORD, {
    onCompleted({ saveTrackRecord }) {
      if (saveTrackRecord) {
        Alert.alert("", "儲存成功", [
          {
            text: "確定",
            onPress: () => {
              onClose("refetch");
            },
            type: "ok",
          },
        ]);
      } else {
        Alert.alert("", "儲存失敗，請再次嘗試。", [
          { text: "確定", type: "ok" },
        ]);
      }
    },
    onError(error) {
      Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
    },
  });
  function save(data) {
    const { statusObject, staff, userId, customerId, ...trackRecordInput } =
      data;
    if (
      trackRecordInput.status === "RESERVED" &&
      trackRecordInput.reservation === null
    ) {
      return Alert.notice("預約日期未填寫！");
    }
    if (trackRecordInput.status === "") {
      return Alert.notice("追蹤狀態未填寫！");
    }

    if (trackRecordInput.cycle === "") trackRecordInput.cycle = "IRREGULARLY";

    if (!!userId) trackRecordInput.userId = userId;
    if (!!customerId) trackRecordInput.customerId = customerId;

    saveTrackRecord({ variables: { trackRecordInput } });
  }
  useEffect(() => {
    if (window.location.pathname.includes("member")) setValue("userId", id);
    else setValue("customerId", id);

    setValue("customerName", name);
    setValue("staffId", Number(localStorage.getItem("@fullhouseUserId")));
    setValue("staff", localStorage.getItem("@fullhouseUserFullName"));
  }, [name]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <LoadingModal loading={loading} />
      <DialogTitle align="center">新增追蹤</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">客戶姓名</Typography>
            <Controller
              name="customerName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextInput {...field} disabled fullWidth />
              )}
            />
          </Grid>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">追蹤業務</Typography>
            <Controller
              name="staff"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextInput {...field} disabled fullWidth />
              )}
            />
          </Grid>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">追蹤日期</Typography>
            <Controller
              name="date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DateInput {...field} format={"yyyy-MM-dd"} fullWidth />
              )}
            />
          </Grid>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">追蹤狀態</Typography>
            <Controller
              name="statusObject"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  items={statusArray}
                  {...field}
                  onChange={(e, v) => {
                    if (v) {
                      setValue("statusObject", v);
                      setValue("status", v.value);
                    } else {
                      setValue("statusObject", { label: "", value: "" });
                      setValue("status", "");
                    }
                  }}
                />
              )}
            />
          </Grid>
          {status === "RESERVED" && (
            <>
              <Grid item sm={5} xs={5}>
                <Typography color="primary">預約日期</Typography>
                <Controller
                  name="reservation"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DateInput {...field} format={"yyyy-MM-dd"} fullWidth />
                  )}
                />
              </Grid>
              <Controller
                name="cycle"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <RadioGroup
                    value={value ?? "不定期"}
                    onChange={onChange}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-end",
                    }}
                  >
                    <FormControlLabel
                      value="IRREGULARLY"
                      control={<Radio />}
                      label="不定期"
                    />
                    <FormControlLabel
                      value="MONTH"
                      control={<Radio />}
                      label="每月新增"
                    />
                    <FormControlLabel
                      value="YEAR"
                      control={<Radio />}
                      label="每年新增"
                    />
                  </RadioGroup>
                )}
              />
            </>
          )}
          <Grid item sm={12} xs={12}>
            <Typography color="primary">追蹤內容</Typography>
            <Controller
              name="content"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} multiline rows={5} fullWidth />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={1} justifyContent="center">
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onPress={handleSubmit(save)}
            >
              儲存
            </Button>
          </Grid>
          <Grid item>
            <Button color="primary" variant="contained" onPress={onClose}>
              返回
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

function SignUpDialog({ id, name, open, onClose = () => {} }) {
  const Alert = useAlert();
  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      courseArray: [],
      courseObject: {},
      name: "",
      customerId: "",
      numberOfPeople: null,
      deposit: 0,
      balancePayment: 0,
      remark: "",
    },
  });
  const [signUpCourse, { loading: signUpCourseLoading }] = useMutation(
    SIGNUPCOURSE,
    {
      onCompleted({ signUpCourse }) {
        if (signUpCourse) {
          Alert.alert("", "報名成功", [
            {
              text: "確定",
              onPress: () => {
                onClose("refetch");
              },
              type: "ok",
            },
          ]);
        } else {
          Alert.alert("", "報名失敗，請再次嘗試。", [
            { text: "確定", type: "ok" },
          ]);
        }
      },
      onError(error) {
        Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
      },
    }
  );
  const { loading: coursesLoading } = useQuery(GET_COURSES, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      latest: false,
      pageSize: 999,
      page: 1,
    },
    //TODO
    onCompleted({ courses }) {
      if (courses) {
        let newArray = [];
        courses.futureContents.map((item) => {
          newArray.push({
            label: `${item.name} ${moment(item.dateStart).format(
              "yyyy-MM-DD"
            )}`,
            value: item.id,
          });
        });
        setValue("courseArray", newArray);
      }
    },
    onError(error) {
      Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
    },
  });
  function save(data) {
    const signUpCourseInput = {
      courseId: data.courseObject.value,
      customerId: data.customerId,
      numberOfPeople: Number(data.numberOfPeople),
      deposit: Number(data.deposit),
      remark: data.remark,
    };
    signUpCourse({ variables: { signUpCourseInputs: [signUpCourseInput] } });
  }
  useEffect(() => {
    setValue("name", name);
    setValue("customerId", id);
  }, [name]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <LoadingModal loading={signUpCourseLoading || coursesLoading} />
      <DialogTitle align="center">預約報名上課</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">報名學員</Typography>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} disabled fullWidth />
              )}
            />
          </Grid>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">選擇報名課程</Typography>
            <Controller
              name="courseObject"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  items={getValues("courseArray")}
                  onChange={(e, v) => {
                    if (v) setValue("courseObject", v);
                    else setValue("courseObject", {});
                  }}
                />
              )}
            />
          </Grid>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">參加人數</Typography>
            <Controller
              name="numberOfPeople"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} fullWidth />
              )}
            />
          </Grid>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">預收訂金</Typography>
            <Controller
              name="deposit"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumberInput {...field} thousandSeparator fullWidth />
              )}
            />
          </Grid>
          <Grid item sm={6} xs={6}>
            <Typography color="primary">應收金額</Typography>
            <Controller
              name="balancePayment"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumberInput {...field} thousandSeparator fullWidth />
              )}
            />
          </Grid>
          <Grid item sm={12} xs={12}>
            <Typography color="primary">備註</Typography>
            <Controller
              name="remark"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} multiline rows={5} fullWidth />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={1} justifyContent="center">
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onPress={handleSubmit(save)}
            >
              儲存
            </Button>
          </Grid>
          <Grid item>
            <Button color="primary" variant="contained" onPress={onClose}>
              返回
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
